'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FormProvider, useForm, useWatch } from 'react-hook-form'
import {
  HiOutlinePencilSquare,
  HiOutlineQuestionMarkCircle,
} from 'react-icons/hi2'
import { toast } from 'sonner'

import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import { SweetspotIcon } from '@/components/common/LogoIcon'
import { OrgPermissionGate } from '@/components/common/PermissionGate'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/common/Select'
import Spinner from '@/components/common/Spinner'
import Switch from '@/components/common/Switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { SalesforceIcon } from '@/components/integrations/Salesforce/Icon'
import { EditStagesDialog } from '@/components/pursuits/v2/components/FilterPanel/Stages/EditStages'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'
import type {
  SalesforceOpportunityStage,
  SalesforceStageMapping,
} from '@/hooks/salesforce/types'
import { useSalesforceIntegration } from '@/hooks/salesforce/useSalesforceIntegration'
import { useHasSalesforceValidationRules } from '@/hooks/salesforce/useSalesforceOpportunityFields'
import { useSalesforceOpportunityStages } from '@/hooks/salesforce/useSalesforceOpportunityStages'
import { useSalesforceStageMappings } from '@/hooks/salesforce/useSalesforceStageMappings'
import { useUpdateSalesforceStageMappings } from '@/hooks/salesforce/useUpdateSalesforceStageMappings'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type {
  SalesforceIntegrationUpdate,
  SalesforceStageMappingCreate,
} from '@/types/Integration'
import type { PursuitStage } from '@/types/pursuit/Stage'

type StageMapping = Record<string, string | undefined>
type AutoSyncMapping = Record<string, boolean | undefined>

const CLEAR_VALUE = '_CLEAR_'

interface FormValues {
  stageMappings: StageMapping
  autoSyncMappings: AutoSyncMapping
  pullSyncEnabled: boolean
}

interface StageSelectProps {
  salesforceStages: SalesforceOpportunityStage[]
  selectedValue?: string
  onChange: (value: string | undefined) => void
}

const StageSelect: React.FC<StageSelectProps> = ({
  salesforceStages,
  selectedValue,
  onChange,
}) => {
  const handleChange = useCallback(
    (value: string) => {
      const adjustedValue =
        value === CLEAR_VALUE || value === '' ? undefined : value
      onChange(adjustedValue)
    },
    [onChange],
  )

  const label = salesforceStages.find(
    (stage) => stage.value === selectedValue,
  )?.label

  return (
    <Select value={selectedValue || ''} onValueChange={handleChange}>
      <SelectTrigger className="w-full h-8">
        {!label ? (
          <span className="text-primary-500 dark:text-primary-400">
            No mapping
          </span>
        ) : (
          <span>{label}</span>
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={CLEAR_VALUE}>
          <span className="text-primary-500 dark:text-primary-400">
            No mapping
          </span>
        </SelectItem>
        {salesforceStages
          .filter((stage) => stage.active)
          .map((stage) => {
            return (
              <SelectItem key={stage.value} value={stage.value}>
                {stage.label}
              </SelectItem>
            )
          })}
      </SelectContent>
    </Select>
  )
}

interface StageMapperErrorProps {
  children: React.ReactNode
}

const StageMapperError: React.FC<StageMapperErrorProps> = ({ children }) => {
  return (
    <div className="text-center py-8 bg-primary-50 dark:bg-primary-900 p-3 rounded-md text-sm text-primary-600 dark:text-primary-400">
      {children}
    </div>
  )
}

interface StageMapperProps {
  orgId: UUID
  stageMappings: StageMapping
  autoSyncMappings: AutoSyncMapping
  onAutoSyncChange: (stageId: UUID, autoSync: boolean) => void
  onStageMappingChange: (
    stageId: UUID,
    salesforceStage: string | undefined,
  ) => void
}

export const StageMapper: React.FC<StageMapperProps> = ({
  orgId,
  stageMappings,
  autoSyncMappings,
  onAutoSyncChange,
  onStageMappingChange,
}) => {
  const { data: pursuitStages, isLoading: isLoadingPursuitStages } =
    usePursuitStages({
      orgId,
    })

  const { data: salesforceStages, isLoading: isLoadingSalesforceStages } =
    useSalesforceOpportunityStages({ orgId })

  const {
    data: hasSalesforceValidationRules,
    isLoading: isLoadingHasSalesforceValidationRules,
  } = useHasSalesforceValidationRules({ orgId })

  const isLoading =
    isLoadingPursuitStages ||
    isLoadingSalesforceStages ||
    isLoadingHasSalesforceValidationRules

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  if (!pursuitStages?.length) {
    return (
      <StageMapperError>
        No pursuit stages found. Please create pursuit stages first.
      </StageMapperError>
    )
  }

  if (!salesforceStages?.length) {
    return (
      <StageMapperError>
        No Salesforce opportunity stages found. Please check your Salesforce
        integration.
      </StageMapperError>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 w-full">
      <div className="grid grid-cols-6 gap-6 font-semibold text-sm text-primary-900 dark:text-primary-100 pb-2 border-b border-primary-200 dark:border-primary-800">
        <div className="col-span-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <SweetspotIcon size={18} />
            <span>Sweetspot Pursuit Stage</span>
          </div>
        </div>

        <div className="col-span-2 flex items-center gap-2">
          <SalesforceIcon size={18} />
          <span>Salesforce Opportunity Stage</span>
        </div>

        <div className="col-span-1 flex items-center gap-2">
          <span>Auto Sync</span>
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <HiOutlineQuestionMarkCircle size={18} />
            </TooltipTrigger>
            <TooltipContent className="max-w-[300px]">
              If Auto Sync is enabled, Sweetspot will automatically sync any
              pursuit that you move to this stage to Salesforce.
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {pursuitStages.map((stage) => {
        const disabled = stageMappings[stage.id] === undefined
        return (
          <div key={stage.id} className="grid grid-cols-6 gap-6 items-center">
            <div className="col-span-3 flex items-center gap-2 text-sm font-medium text-primary-900 dark:text-primary-100">
              <ColorIndicator color={stage.color} />
              <span>{stage.name}</span>
            </div>
            <div className="col-span-2 flex items-center gap-2 w-full">
              <div className="flex items-center gap-2 w-full">
                <StageSelect
                  salesforceStages={salesforceStages || []}
                  selectedValue={stageMappings[stage.id]}
                  onChange={(salesforceStage) =>
                    onStageMappingChange(stage.id, salesforceStage)
                  }
                />
              </div>
            </div>
            <div className="col-span-1 flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger
                    type="button"
                    className={disabled ? 'cursor-not-allowed' : ''}
                  >
                    <Switch
                      checked={autoSyncMappings[stage.id] || false}
                      onClick={() =>
                        onAutoSyncChange(stage.id, !autoSyncMappings[stage.id])
                      }
                      disabled={disabled || hasSalesforceValidationRules}
                    />
                  </TooltipTrigger>
                  {hasSalesforceValidationRules ? (
                    <TooltipContent className="max-w-[300px]" side="right">
                      Auto Sync is unavailable because your Salesforce
                      Opportunity configuration has validation rules in place.
                      As a result, Sweetspot cannot automatically sync pursuits
                      to Salesforce. To sync a pursuit, please use the{' '}
                      <b>Sync to Salesforce</b> button on the pursuit overview
                      page.
                    </TooltipContent>
                  ) : disabled ? (
                    <TooltipContent className="max-w-[300px]" side="right">
                      No Salesforce Opportunity stage mapped. Please map a
                      Salesforce Opportunity stage to this pursuit stage to
                      enable auto sync.
                    </TooltipContent>
                  ) : null}
                </Tooltip>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface StageMapperDialogProps {
  orgId: UUID
  open: boolean
  onOpenChange: (open: boolean) => void
}

const selectStageMappings = (
  data: PaginatedResponse<SalesforceStageMapping>,
) => {
  const stageMappings = data.data.reduce(
    (acc, mapping) => ({
      ...acc,
      [mapping.pursuit_stage_id]: mapping.salesforce_stage_name,
    }),
    {} as StageMapping,
  )
  const autoSyncMappings = data.data.reduce(
    (acc, mapping) => ({
      ...acc,
      [mapping.pursuit_stage_id]: mapping.auto_sync,
    }),
    {} as AutoSyncMapping,
  )
  return { stageMappings, autoSyncMappings }
}

const onOpenAutoFocus = (event: Event) => {
  event.preventDefault()
}

export const StageMapperDialog: React.FC<StageMapperDialogProps> = ({
  orgId,
  open,
  onOpenChange,
}) => {
  const { data: integration, isSuccess: isSuccessIntegration } =
    useSalesforceIntegration({ orgId })
  const { data, isSuccess: isSuccessExistingMappings } =
    useSalesforceStageMappings({ orgId, select: selectStageMappings })
  const { mutateAsync: updateMappings, isPending } =
    useUpdateSalesforceStageMappings({ orgId })

  const isSuccess = isSuccessIntegration && isSuccessExistingMappings

  const loadedFormValues = useMemo<FormValues>(
    () => ({
      stageMappings: data?.stageMappings ?? {},
      autoSyncMappings: data?.autoSyncMappings ?? {},
      pullSyncEnabled: integration?.pull_sync_enabled ?? false,
    }),
    [
      data?.stageMappings,
      data?.autoSyncMappings,
      integration?.pull_sync_enabled,
    ],
  )

  const form = useForm<FormValues>({
    defaultValues: loadedFormValues,
  })

  const { control, getValues, handleSubmit, reset, setValue } = form
  const hydratedOpenFormRef = useRef(false)

  const stageMappings = useWatch({ control, name: 'stageMappings' }) ?? {}
  const autoSyncMappings = useWatch({ control, name: 'autoSyncMappings' }) ?? {}
  const pullSyncEnabled = useWatch({ control, name: 'pullSyncEnabled' })

  const handleStageMappingChange = useCallback(
    (stageId: UUID, salesforceStage: string | undefined) => {
      setValue(
        'stageMappings',
        {
          ...(getValues('stageMappings') ?? {}),
          [stageId]: salesforceStage,
        },
        { shouldDirty: true, shouldTouch: true },
      )
    },
    [getValues, setValue],
  )

  const handleAutoSyncChange = useCallback(
    (stageId: UUID, autoSync: boolean) => {
      setValue(
        'autoSyncMappings',
        {
          ...(getValues('autoSyncMappings') ?? {}),
          [stageId]: autoSync,
        },
        { shouldDirty: true, shouldTouch: true },
      )
    },
    [getValues, setValue],
  )

  const handlePullSyncChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue('pullSyncEnabled', event.currentTarget.checked, {
        shouldDirty: true,
      })
    },
    [setValue],
  )

  const [editStagesDialogOpen, setEditStagesDialogOpen] =
    useState<boolean>(false)

  const handleEditStagesDialogOpen = useCallback(() => {
    setEditStagesDialogOpen(true)
  }, [setEditStagesDialogOpen])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange(open)
      if (open) {
        reset(loadedFormValues)
        hydratedOpenFormRef.current = isSuccess
      } else {
        hydratedOpenFormRef.current = false
      }
    },
    [isSuccess, loadedFormValues, onOpenChange, reset],
  )

  const handleCancel = useCallback(() => {
    onOpenChange(false)
    reset(loadedFormValues)
  }, [loadedFormValues, onOpenChange, reset])

  const onSubmit = useCallback(
    async (data: FormValues) => {
      if (!integration) {
        toast.error('Salesforce integration is not ready. Please try again.')
        return
      }

      const mappingsArray: SalesforceStageMappingCreate[] = Object.entries(
        data.stageMappings,
      )
        .filter(([_, salesforceStage]) => !!salesforceStage)
        .map(([pursuitStageId, salesforceStage]) => ({
          pursuit_stage_id: pursuitStageId as UUID,
          salesforce_stage_name: salesforceStage as string,
          auto_sync: data.autoSyncMappings[pursuitStageId] || false,
        }))

      try {
        const payload: SalesforceIntegrationUpdate = {
          stage_mappings: mappingsArray,
          pull_sync_enabled: data.pullSyncEnabled || false,
          pursuit_extended_schema_id: integration.pursuit_extended_schema_id,
          field_mappings: integration.field_mappings,
        }
        await updateMappings(payload)
        reset(data)
        toast.success('Salesforce settings updated successfully!')
        onOpenChange(false)
      } catch (error) {
        console.error('Error updating Salesforce settings:', error)
      }
    },
    [getValues, updateMappings, onOpenChange, integration],
  )

  const handleEditStagesDialogSuccess = useCallback(
    (data: PursuitStage[]) => {
      const newMappings = data.reduce(
        (acc, stage) => ({
          ...acc,
          [stage.id]: loadedFormValues.stageMappings[stage.id],
        }),
        {} as StageMapping,
      )
      reset({ ...getValues(), stageMappings: newMappings })
    },
    [getValues, loadedFormValues.stageMappings, reset],
  )

  useEffect(() => {
    if (!open) {
      hydratedOpenFormRef.current = false
      return
    }

    if (isSuccess && !hydratedOpenFormRef.current) {
      hydratedOpenFormRef.current = true
      reset(loadedFormValues)
    }
  }, [open, isSuccess, loadedFormValues, reset])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-full max-w-5xl"
        onOpenAutoFocus={onOpenAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="mb-4">
            <div className="flex items-center gap-2">
              <SalesforceIcon size={18} />
              Configure Your Salesforce Integration
            </div>
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <p>
              Configure how your Sweetspot Pursuit stages map to Salesforce
              Opportunity stages. Stages that are not mapped will default to the
              first stage in your Salesforce account.
            </p>
            <p>
              <strong>Note:</strong> if your Salesforce Opportunity stages
              change after you&apos;ve already mapped them, you&apos;ll need to
              remember to update these mappings again.
            </p>
            <p>Sweetspot will sync with Salesforce every hour.</p>
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          {/* we have this dialog outside of the form provider since it also contains a form */}
          <EditStagesDialog
            open={editStagesDialogOpen}
            onOpenChange={setEditStagesDialogOpen}
            onSuccess={handleEditStagesDialogSuccess}
          />
          <form onSubmit={handleSubmit(onSubmit)} className="mt-2">
            <div className="py-4">
              <StageMapper
                orgId={orgId}
                stageMappings={stageMappings}
                autoSyncMappings={autoSyncMappings}
                onAutoSyncChange={handleAutoSyncChange}
                onStageMappingChange={handleStageMappingChange}
              />
            </div>
            <div className="flex flex-col gap-2 py-4 divide-y divide-primary-200 dark:divide-primary-800">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  Salesforce Opportunity Sync Settings
                </span>
              </div>
              <div className="flex items-center gap-2 pt-4">
                <Checkbox
                  id="pull-sync-enabled"
                  checked={pullSyncEnabled || false}
                  onChange={handlePullSyncChange}
                  childrenOnly={true}
                />
                <label
                  htmlFor="pull-sync-enabled"
                  className="text-sm select-none"
                >
                  Automatically pull opportunities from Salesforce
                </label>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <HiOutlineQuestionMarkCircle size={18} />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px]">
                    If enabled, Sweetspot will automatically pull opportunities
                    from Salesforce into Sweetspot every hour.
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <DialogFooter className="flex items-center justify-between gap-2 mt-6">
              <OrgPermissionGate
                permission="admin"
                fallback="disable"
                tooltip="Only admins can edit pursuit stages"
                asChild
              >
                <Button
                  type="button"
                  variant="outline-compact"
                  onClick={handleEditStagesDialogOpen}
                >
                  <HiOutlinePencilSquare size={18} />
                  <span>Edit Pursuit Stages</span>
                </Button>
              </OrgPermissionGate>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline-compact"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary-compact"
                  disabled={isPending}
                  isLoading={isPending}
                >
                  Save Salesforce Settings
                </Button>
              </div>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
